/**
 * Money for the investment table — integer minor units only.
 *
 * PLANNING-CONTRACT §2: "calculations use integer minor units or exact
 * decimals, never binary floating-point currency arithmetic." The server
 * speaks decimal strings matching /^\d+(\.\d{1,2})?$/, so `1250`, `1250.5`
 * and `1250.00` are all legal wire values for the same amount. Everything in
 * this module converts to `number` minor units at the boundary, adds there,
 * and formats back — no float ever holds a currency value.
 *
 * `null` is **Unbudgeted**, which is not a budget of zero (§2). The two render
 * differently and the distinction survives every function here: a null in
 * gives a null out, never a 0.
 */

/** A signed integer count of the currency's smallest unit (cents for USD). */
export type Minor = number;

const MONEY_RE = /^-?\d+(\.\d{1,2})?$/;

export function isMoneyString(raw: string): boolean {
  return MONEY_RE.test(raw.trim());
}

/**
 * Wire decimal string -> minor units. Pads the fraction to exactly two places
 * first, so `1250`, `1250.5` and `1250.50` do not land on three different
 * numbers. Throws on anything the server would not have sent, because a
 * silently-wrong total in a budget meeting is worse than a visible error.
 */
export function toMinor(raw: string): Minor {
  const s = raw.trim();
  if (!isMoneyString(s)) throw new Error(`Not a money amount: ${raw}`);
  const negative = s.startsWith('-');
  const [whole, frac = ''] = (negative ? s.slice(1) : s).split('.');
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  return negative ? -cents : cents;
}

/** Minor units -> the canonical two-place decimal string the API accepts. */
export function fromMinor(minor: Minor): string {
  const negative = minor < 0;
  const abs = Math.abs(Math.trunc(minor));
  const body = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  return negative ? `-${body}` : body;
}

/** null-tolerant parse: Unbudgeted stays Unbudgeted. */
export function toMinorOrNull(raw: string | null | undefined): Minor | null {
  return raw === null || raw === undefined || raw === '' ? null : toMinor(raw);
}

export function sumMinor(values: readonly Minor[]): Minor {
  let total = 0;
  for (const v of values) total += v;
  return total;
}

/**
 * Equal distribution with the contract's remainder rule (§2): "For remainder
 * cents, assign one cent at a time in chronological order. For example,
 * $100.00 across three months is $33.34/$33.33/$33.33."
 *
 * The server owns the authoritative distribution. This exists so the table can
 * show the *preview* a user sees while typing, and so the rule is asserted
 * once, here, against the contract's own worked example.
 */
export function distributeMinor(total: Minor, slots: number): Minor[] {
  if (slots <= 0) return [];
  const base = Math.trunc(total / slots);
  const remainder = total - base * slots;
  const step = remainder >= 0 ? 1 : -1;
  let left = Math.abs(remainder);
  return Array.from({ length: slots }, () => {
    const extra = left > 0 ? step : 0;
    if (left > 0) left -= 1;
    return base + extra;
  });
}

/**
 * Display formatting. Dense table cells get grouped digits with no currency
 * symbol (the column group carries the currency, and §2 forbids adding unlike
 * currencies so a column is never mixed); headers and totals get the symbol.
 */
export function formatMinor(
  minor: Minor,
  opts: { currency?: string | null; compact?: boolean; decimals?: boolean } = {},
): string {
  const { currency, compact = false, decimals = true } = opts;
  const value = minor / 100;
  const fraction = decimals && Math.abs(minor % 100) > 0 ? 2 : decimals && !compact ? 2 : 0;
  const format: Intl.NumberFormatOptions = {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
    ...(compact ? { notation: 'compact', maximumFractionDigits: 1, minimumFractionDigits: 0 } : {}),
    ...(currency ? { style: 'currency', currency, currencyDisplay: 'narrowSymbol' } : {}),
  };
  try {
    return new Intl.NumberFormat(undefined, format).format(value);
  } catch {
    // An unconfigured currency code must not blank the meeting screen.
    return `${fromMinor(minor)}${currency ? ` ${currency}` : ''}`;
  }
}

/** The one place the Unbudgeted label is written, so it reads the same everywhere. */
export const UNBUDGETED_LABEL = 'Unbudgeted';

/**
 * A cell's display string. Distinguishes the three states the contract insists
 * are different: no amount at all (Unbudgeted), a real zero, and an amount.
 */
export function cellLabel(minor: Minor | null, opts: { currency?: string | null; compact?: boolean } = {}): string {
  if (minor === null) return UNBUDGETED_LABEL;
  return formatMinor(minor, opts);
}

/** Sum a currency-keyed map of wire amounts into minor units, keeping groups apart. */
export function byCurrencyToMinor(map: Record<string, string> | undefined | null): Record<string, Minor> {
  const out: Record<string, Minor> = {};
  for (const [currency, amount] of Object.entries(map ?? {})) out[currency] = toMinor(amount);
  return out;
}
